import { Pencil } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Features } from '@/lib/feature-flags'
import { CompaniesService } from '@/services/companies'
import { HourTypesService } from '@/services/hour-types'
import { NextCodesService } from '@/services/next-codes'
import { ProjectsService } from '@/services/projects'
import { TimesheetsService } from '@/services/timesheets'
import { UsersService } from '@/services/users'
import { useBoundStore } from '@/store/useBoundStore'
import type { Company } from '@/types/company'
import type { HourType } from '@/types/hour-type'
import type { Project } from '@/types/project'
import type { Timesheet } from '@/types/timesheet'
import type { User } from '@/types/user'
import { CompanyHourTypesTab } from './company/CompanyHourTypesTab'
import { CompanyProjectsTab } from './company/CompanyProjectsTab'
import { CompanyTimesheetsTab } from './company/CompanyTimesheetsTab'
import { CompanyUsersTab } from './company/CompanyUsersTab'
import { DeleteConfirmDialog } from './company/DeleteConfirmDialog'
import { TimesheetDetailDialog } from './company/TimesheetDetailDialog'
import { UserOptionsSheet } from './company/UserOptionsSheet'

interface CompanyPageProps {
	company: Company
	onBack: () => void
	onCompanyUpdate?: (company: Company) => void
}

export function CompanyPage({ company: initialCompany, onBack, onCompanyUpdate }: CompanyPageProps) {
	const isAdmin = useBoundStore((state) => state.isAdmin)
	const isManager = useBoundStore((state) => state.isManager)
	const canManage = isAdmin() || isManager()
	const [company, setCompany] = useState<Company>(initialCompany)

	const [activeTab, setActiveTab] = useState('users')
	const [users, setUsers] = useState<User[]>([])
	const [hourTypes, setHourTypes] = useState<HourType[]>([])
	const [projects, setProjects] = useState<Project[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [timesheets, setTimesheets] = useState<Timesheet[]>([])
	const [timesheetFilters, setTimesheetFilters] = useState<{
		from?: string
		to?: string
		date?: string
		userID?: string
	}>({})

	// Estados para dialogs y sheets
	const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet | null>(null)
	const [isTimesheetDialogOpen, setIsTimesheetDialogOpen] = useState(false)

	// Estados para editar empresa
	const [isCompanySheetOpen, setIsCompanySheetOpen] = useState(false)
	const [companyFormName, setCompanyFormName] = useState(company.name)
	const [companyFormActive, setCompanyFormActive] = useState(company.active)
	const [companySaving, setCompanySaving] = useState(false)
	const [companyFeatures, setCompanyFeatures] = useState<Partial<Features>>({})
	const [featuresLoading, setFeaturesLoading] = useState(false)

	// Estados para usuarios
	const [isUserSheetOpen, setIsUserSheetOpen] = useState(false)
	const [isCreatingUser, setIsCreatingUser] = useState(false)
	const [selectedUser, setSelectedUser] = useState<User | null>(null)
	const [userFormCode, setUserFormCode] = useState('')
	const [userFormName, setUserFormName] = useState('')
	const [userFormEmail, setUserFormEmail] = useState('')
	const [userFormPassword, setUserFormPassword] = useState('')
	const [userFormIsManager, setUserFormIsManager] = useState(false)
	const [userSaving, setUserSaving] = useState(false)

	// Estados para tipos de hora
	const [isHourTypeSheetOpen, setIsHourTypeSheetOpen] = useState(false)
	const [isCreatingHourType, setIsCreatingHourType] = useState(false)
	const [selectedHourType, setSelectedHourType] = useState<HourType | null>(null)
	const [hourTypeFormCode, setHourTypeFormCode] = useState('')
	const [hourTypeFormName, setHourTypeFormName] = useState('')
	const [hourTypeFormComputable, setHourTypeFormComputable] = useState(false)
	const [hourTypeSaving, setHourTypeSaving] = useState(false)

	// Estados para proyectos
	const [isProjectSheetOpen, setIsProjectSheetOpen] = useState(false)
	const [isCreatingProject, setIsCreatingProject] = useState(false)
	const [selectedProject, setSelectedProject] = useState<Project | null>(null)
	const [projectFormCode, setProjectFormCode] = useState('')
	const [projectFormName, setProjectFormName] = useState('')
	const [projectSaving, setProjectSaving] = useState(false)

	// Estados para Alert Dialogs
	const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false)
	const [deleteHourTypeDialogOpen, setDeleteHourTypeDialogOpen] = useState(false)
	const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState(false)
	const [itemToDelete, setItemToDelete] = useState<{ type: 'user' | 'hourType' | 'project'; id: string } | null>(null)

	// Estados para opciones de usuario
	const [isUserOptionsSheetOpen, setIsUserOptionsSheetOpen] = useState(false)
	const [userOptionsUser, setUserOptionsUser] = useState<User | null>(null)

	const fetchUsers = useCallback(async () => {
		try {
			setLoading(true)
			setError(null)
			const usersData = await UsersService.getUsers(company.id)
			setUsers(usersData || [])
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Error al cargar usuarios')
		} finally {
			setLoading(false)
		}
	}, [company.id])

	const fetchHourTypes = useCallback(async () => {
		try {
			setLoading(true)
			setError(null)
			const hourTypesData = await HourTypesService.getHourTypesByCompany(company.id)
			setHourTypes(hourTypesData || [])
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Error al cargar tipos de hora')
		} finally {
			setLoading(false)
		}
	}, [company.id])

	const fetchProjects = useCallback(async () => {
		try {
			setLoading(true)
			setError(null)
			const projectsData = await ProjectsService.getProjectsByCompany(company.id)
			setProjects(projectsData || [])
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Error al cargar proyectos')
		} finally {
			setLoading(false)
		}
	}, [company.id])

	const fetchTimesheets = useCallback(async () => {
		try {
			setLoading(true)
			setError(null)
			const data = await TimesheetsService.getTimesheets(timesheetFilters, company.id)
			setTimesheets(data || [])
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Error al cargar registros horarios')
		} finally {
			setLoading(false)
		}
	}, [company.id, timesheetFilters])

	useEffect(() => {
		if (activeTab === 'users') {
			fetchUsers()
			fetchHourTypes()
			fetchProjects()
		} else if (activeTab === 'hour-types') {
			fetchHourTypes()
		} else if (activeTab === 'projects') {
			fetchProjects()
		} else if (activeTab === 'timesheets') {
			fetchHourTypes()
			fetchProjects()
			fetchTimesheets()
		}
	}, [activeTab, company.id, fetchUsers, fetchHourTypes, fetchProjects, fetchTimesheets])

	useEffect(() => {
		setCompany(initialCompany)
		setCompanyFormName(initialCompany.name)
		setCompanyFormActive(initialCompany.active)
	}, [initialCompany])

	// Funciones para editar empresa
	const handleEditCompany = async () => {
		setCompanyFormName(company.name)
		setCompanyFormActive(company.active)
		setIsCompanySheetOpen(true)
		// Cargar features cuando se abre el sheet
		try {
			setFeaturesLoading(true)
			const response = await CompaniesService.getCompanyFeatures(company.id)
			setCompanyFeatures(response.features as Partial<Features>)
		} catch (err) {
			console.error('Error al cargar features:', err)
			setCompanyFeatures({})
		} finally {
			setFeaturesLoading(false)
		}
	}

	const handleFeatureChange = async (feature: string, value: string) => {
		try {
			await CompaniesService.setCompanyFeature(company.id, feature, value)
			setCompanyFeatures((prev) => ({ ...prev, [feature]: value }))
			toast.success('Feature actualizada correctamente')
		} catch (err) {
			console.error('Error al actualizar feature:', err)
			toast.error(err instanceof Error ? err.message : 'Error al actualizar feature')
		}
	}

	const handleSaveCompany = async () => {
		try {
			setCompanySaving(true)
			setError(null)
			const updated = await CompaniesService.updateCompany({
				id: company.id,
				code: company.code,
				name: companyFormName,
				active: companyFormActive,
			})
			setCompany(updated)
			setIsCompanySheetOpen(false)
			toast.success('Empresa actualizada correctamente')
			if (onCompanyUpdate) {
				onCompanyUpdate(updated)
			}
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Error al actualizar empresa'
			setError(errorMessage)
		} finally {
			setCompanySaving(false)
		}
	}

	// Funciones para usuarios
	const handleCreateUser = async () => {
		setSelectedUser(null)
		setIsCreatingUser(true)
		setUserFormCode('')
		setUserFormName('')
		setUserFormEmail('')
		setUserFormPassword('')
		setUserFormIsManager(false)
		setError(null)
		setIsUserSheetOpen(true)
		try {
			const codes = await NextCodesService.getNextCodes(company.id)
			setUserFormCode(codes.user)
		} catch {
			// Silenciar: el usuario puede escribir el código manualmente
		}
	}

	const handleEditUser = (user: User) => {
		setSelectedUser(user)
		setIsCreatingUser(false)
		setUserFormCode(user.code)
		setUserFormName(user.name)
		setUserFormEmail(user.email || '')
		setUserFormPassword('')
		setUserFormIsManager(user.isManager)
		setError(null)
		setIsUserSheetOpen(true)
	}

	const handleSaveUser = async () => {
		try {
			setUserSaving(true)
			setError(null)

			if (isCreatingUser) {
				await UsersService.createUser({
					code: userFormCode,
					name: userFormName,
					email: userFormEmail,
					password: userFormPassword,
					companyID: company.id,
					role: userFormIsManager ? 'manager' : 'employee',
				})
				toast.success('Usuario creado correctamente')
			} else if (selectedUser) {
				const updateData: {
					id: string
					companyID: string
					name: string
					email: string
					role: 'manager' | 'employee'
					code: string
					password?: string
				} = {
					id: selectedUser.id,
					companyID: company.id,
					name: userFormName,
					email: userFormEmail,
					role: userFormIsManager ? 'manager' : 'employee',
					code: userFormCode,
				}
				if (userFormPassword.trim()) {
					updateData.password = userFormPassword
				}
				await UsersService.updateUser(updateData)
				toast.success('Usuario actualizado correctamente')
			}

			setIsUserSheetOpen(false)
			await fetchUsers()
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: isCreatingUser
						? 'Error al crear usuario'
						: 'Error al actualizar usuario'
			setError(errorMessage)
		} finally {
			setUserSaving(false)
		}
	}

	const handleDeleteUser = (userId: string) => {
		setItemToDelete({ type: 'user', id: userId })
		setDeleteUserDialogOpen(true)
	}

	const confirmDeleteUser = async () => {
		if (!itemToDelete || itemToDelete.type !== 'user') return
		try {
			await UsersService.deleteUser(itemToDelete.id)
			toast.success('Usuario eliminado correctamente')
			await fetchUsers()
			setDeleteUserDialogOpen(false)
			setItemToDelete(null)
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Error al eliminar usuario'
			setError(errorMessage)
			setDeleteUserDialogOpen(false)
			setItemToDelete(null)
		}
	}

	// Funciones para tipos de hora
	const handleCreateHourType = async () => {
		setSelectedHourType(null)
		setIsCreatingHourType(true)
		setHourTypeFormCode('')
		setHourTypeFormName('')
		setHourTypeFormComputable(false)
		setError(null)
		setIsHourTypeSheetOpen(true)
		try {
			const codes = await NextCodesService.getNextCodes(company.id)
			setHourTypeFormCode(codes.hourType)
		} catch {
			// Silenciar: el usuario puede escribir el código manualmente
		}
	}

	const handleEditHourType = (hourType: HourType) => {
		setSelectedHourType(hourType)
		setIsCreatingHourType(false)
		setHourTypeFormCode(hourType.code)
		setHourTypeFormName(hourType.name)
		setHourTypeFormComputable(hourType.computable)
		setError(null)
		setIsHourTypeSheetOpen(true)
	}

	const handleSaveHourType = async () => {
		try {
			setHourTypeSaving(true)
			setError(null)

			if (isCreatingHourType) {
				await HourTypesService.createHourType({
					companyID: company.id,
					code: hourTypeFormCode,
					name: hourTypeFormName,
					computable: hourTypeFormComputable,
				})
				toast.success('Tipo de hora creado correctamente')
			} else if (selectedHourType) {
				await HourTypesService.updateHourType({
					id: selectedHourType.id,
					code: hourTypeFormCode,
					companyID: company.id,
					name: hourTypeFormName,
					computable: hourTypeFormComputable,
				})
				toast.success('Tipo de hora actualizado correctamente')
			}

			setIsHourTypeSheetOpen(false)
			await fetchHourTypes()
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: isCreatingHourType
						? 'Error al crear tipo de hora'
						: 'Error al actualizar tipo de hora'
			setError(errorMessage)
		} finally {
			setHourTypeSaving(false)
		}
	}

	const handleDeleteHourType = (hourTypeId: string) => {
		setItemToDelete({ type: 'hourType', id: hourTypeId })
		setDeleteHourTypeDialogOpen(true)
	}

	const confirmDeleteHourType = async () => {
		if (!itemToDelete || itemToDelete.type !== 'hourType') return
		try {
			await HourTypesService.deleteHourType(itemToDelete.id)
			toast.success('Tipo de hora eliminado correctamente')
			await fetchHourTypes()
			setDeleteHourTypeDialogOpen(false)
			setItemToDelete(null)
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Error al eliminar tipo de hora'
			setError(errorMessage)
			setDeleteHourTypeDialogOpen(false)
			setItemToDelete(null)
		}
	}

	// Funciones para proyectos
	const handleCreateProject = async () => {
		setSelectedProject(null)
		setIsCreatingProject(true)
		setProjectFormCode('')
		setProjectFormName('')
		setError(null)
		setIsProjectSheetOpen(true)
		try {
			const codes = await NextCodesService.getNextCodes(company.id)
			setProjectFormCode(codes.project)
		} catch {
			// Silenciar: el usuario puede escribir el código manualmente
		}
	}

	const handleEditProject = (project: Project) => {
		setSelectedProject(project)
		setIsCreatingProject(false)
		setProjectFormCode(project.code)
		setProjectFormName(project.name)
		setError(null)
		setIsProjectSheetOpen(true)
	}

	const handleSaveProject = async () => {
		try {
			setProjectSaving(true)
			setError(null)

			if (isCreatingProject) {
				await ProjectsService.createProject({
					companyID: company.id,
					code: projectFormCode,
					name: projectFormName,
				})
				toast.success('Proyecto creado correctamente')
			} else if (selectedProject) {
				await ProjectsService.updateProject({
					id: selectedProject.id,
					code: projectFormCode,
					companyID: company.id,
					name: projectFormName,
				})
				toast.success('Proyecto actualizado correctamente')
			}

			setIsProjectSheetOpen(false)
			await fetchProjects()
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: isCreatingProject
						? 'Error al crear proyecto'
						: 'Error al actualizar proyecto'
			setError(errorMessage)
		} finally {
			setProjectSaving(false)
		}
	}

	const handleDeleteProject = (projectId: string) => {
		setItemToDelete({ type: 'project', id: projectId })
		setDeleteProjectDialogOpen(true)
	}

	const confirmDeleteProject = async () => {
		if (!itemToDelete || itemToDelete.type !== 'project') return
		try {
			await ProjectsService.deleteProject(itemToDelete.id)
			toast.success('Proyecto eliminado correctamente')
			await fetchProjects()
			setDeleteProjectDialogOpen(false)
			setItemToDelete(null)
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Error al eliminar proyecto'
			setError(errorMessage)
			setDeleteProjectDialogOpen(false)
			setItemToDelete(null)
		}
	}

	const handleUserOptions = (user: User) => {
		setUserOptionsUser(user)
		setIsUserOptionsSheetOpen(true)
	}

	const handleTimesheetSelect = (timesheet: Timesheet) => {
		setSelectedTimesheet(timesheet)
		setIsTimesheetDialogOpen(true)
	}

	const handleTimesheetUpdate = (updated: Timesheet) => {
		setSelectedTimesheet(updated)
		setTimesheets((prev) => prev.map((ts) => (ts.id === updated.id ? updated : ts)))
	}

	return (
		<div className="container mx-auto p-2">
			<div className="max-w-6xl mx-auto space-y-6 relative">
				{isAdmin() && onBack && (
					<Button
						variant="ghost"
						onClick={onBack}
						className="absolute top-0 left-0 bg-muted hover:bg-muted/80 cursor-pointer"
					>
						← Volver
					</Button>
				)}
				{canManage ? (
					<Tabs value={activeTab} onValueChange={setActiveTab}>
						<div className="flex items-center justify-between pt-10 gap-6">
							<div className="flex items-center gap-3">
								<div>
									<h1 className="text-3xl font-bold">{company.name}</h1>
									<p className="text-muted-foreground">Código: {company.code}</p>
								</div>
								{isAdmin() && (
									<Button variant="ghost" size="icon" onClick={handleEditCompany}>
										<Pencil className="h-4 w-4" />
									</Button>
								)}
							</div>
							<div className="flex items-center gap-4">
								<Badge
									variant={company.active ? 'default' : 'outline'}
									className={
										company.active
											? 'bg-green-500/10 text-green-500 border-green-500/20'
											: 'bg-gray-500/10 text-gray-500 border-gray-500/20'
									}
								>
									{company.active ? 'Activa' : 'Inactiva'}
								</Badge>
								<TabsList>
									<TabsTrigger className="cursor-pointer" value="users">
										Usuarios
									</TabsTrigger>
									<TabsTrigger className="cursor-pointer" value="hour-types">
										Tipos de hora
									</TabsTrigger>
									<TabsTrigger className="cursor-pointer" value="projects">
										Proyectos
									</TabsTrigger>
									<TabsTrigger className="cursor-pointer" value="timesheets">
										Registros horarios
									</TabsTrigger>
								</TabsList>
							</div>
						</div>

						{error && (
							<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
								{error}
							</div>
						)}

						<TabsContent value="users" className="space-y-4">
							<CompanyUsersTab
								users={users}
								loading={loading}
								onCreate={handleCreateUser}
								onEdit={handleEditUser}
								onDelete={handleDeleteUser}
								onOptions={handleUserOptions}
							/>
						</TabsContent>

						<TabsContent value="hour-types" className="space-y-4">
							<CompanyHourTypesTab
								hourTypes={hourTypes}
								loading={loading}
								onCreate={handleCreateHourType}
								onEdit={handleEditHourType}
								onDelete={handleDeleteHourType}
							/>
						</TabsContent>

						<TabsContent value="projects" className="space-y-4">
							<CompanyProjectsTab
								projects={projects}
								loading={loading}
								onCreate={handleCreateProject}
								onEdit={handleEditProject}
								onDelete={handleDeleteProject}
							/>
						</TabsContent>

						<TabsContent value="timesheets" className="space-y-4">
							<CompanyTimesheetsTab
								timesheets={timesheets}
								users={users}
								loading={loading}
								error={error}
								filters={timesheetFilters}
								onFiltersChange={setTimesheetFilters}
								onTimesheetSelect={handleTimesheetSelect}
								company={company}
								projects={projects}
								hourTypes={hourTypes}
								onTimesheetCreated={() => {
									fetchTimesheets()
								}}
							/>
						</TabsContent>
					</Tabs>
				) : (
					<>
						<div className="flex items-center justify-between pt-12 gap-6">
							<div className="flex items-center gap-3">
								<div>
									<h1 className="text-3xl font-bold">{company.name}</h1>
									<p className="text-muted-foreground">Código: {company.code}</p>
								</div>
							</div>
							<Badge
								variant={company.active ? 'default' : 'outline'}
								className={
									company.active
										? 'bg-green-500/10 text-green-500 border-green-500/20'
										: 'bg-gray-500/10 text-gray-500 border-gray-500/20'
								}
							>
								{company.active ? 'Activa' : 'Inactiva'}
							</Badge>
						</div>

						{error && (
							<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
								{error}
							</div>
						)}
					</>
				)}

				{/* Sheet para editar empresa */}
				<Sheet
					open={isCompanySheetOpen}
					onOpenChange={(open) => {
						setIsCompanySheetOpen(open)
						if (!open) {
							setError(null)
						}
					}}
				>
					<SheetContent>
						<SheetHeader className="space-y-2 pb-4">
							<SheetTitle>Editar empresa</SheetTitle>
							<SheetDescription>
								Modifica los datos de la empresa. Los cambios se guardarán al hacer clic en Guardar.
							</SheetDescription>
						</SheetHeader>
						<div className="flex-1 py-4 space-y-4 px-4">
							<div className="space-y-2">
								<Label htmlFor="company-code">Código</Label>
								<Input id="company-code" value={company.code} disabled className="bg-muted" />
							</div>
							<div className="space-y-2">
								<Label htmlFor="company-name">Nombre</Label>
								<Input
									id="company-name"
									value={companyFormName}
									onChange={(e) => setCompanyFormName(e.target.value)}
									placeholder="Nombre de la empresa"
									required
								/>
							</div>
							<div className="flex items-center justify-between p-3 rounded-lg border bg-card">
								<Label htmlFor="company-active" className="cursor-pointer flex-1">
									Empresa activa
								</Label>
								<Switch
									id="company-active"
									checked={companyFormActive}
									onCheckedChange={setCompanyFormActive}
								/>
							</div>

							{/* Feature Flags */}
							<div className="space-y-4 pt-4 border-t">
								<Label className="text-base font-semibold">Configuración</Label>
								{featuresLoading ? (
									<div className="text-sm text-muted-foreground">Cargando configuración...</div>
								) : (
									<div className="space-y-3">
										<div className="flex items-center justify-between p-3 rounded-lg border bg-card">
											<div className="flex-1">
												<Label
													htmlFor="geolocation-required"
													className="cursor-pointer font-medium"
												>
													Geolocalización requerida
												</Label>
												<p className="text-xs text-muted-foreground mt-1">
													Requiere geolocalización para registrar horas
												</p>
											</div>
											<Switch
												id="geolocation-required"
												checked={companyFeatures.geolocationRequired === 'on'}
												onCheckedChange={(checked) => {
													console.log('Checked:', checked)
													handleFeatureChange('geolocationRequired', checked ? 'on' : 'off')
												}}
											/>
										</div>
									</div>
								)}
							</div>

							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}
						</div>
						<SheetFooter className="pt-4">
							<Button onClick={handleSaveCompany} disabled={companySaving || !companyFormName.trim()}>
								{companySaving ? 'Guardando...' : 'Guardar cambios'}
							</Button>
						</SheetFooter>
					</SheetContent>
				</Sheet>

				{/* Sheet para crear/editar usuario */}
				<Sheet
					open={isUserSheetOpen}
					onOpenChange={(open) => {
						setIsUserSheetOpen(open)
						if (!open) {
							setError(null)
							setIsCreatingUser(false)
							setSelectedUser(null)
						}
					}}
				>
					<SheetContent>
						<SheetHeader className="space-y-2 pb-4">
							<SheetTitle>{isCreatingUser ? 'Crear usuario' : 'Editar usuario'}</SheetTitle>
							<SheetDescription>
								{isCreatingUser
									? 'Completa los datos para crear un nuevo usuario.'
									: 'Modifica los datos del usuario. Los cambios se guardarán al hacer clic en Guardar.'}
							</SheetDescription>
						</SheetHeader>
						<div className="flex-1 py-4 space-y-4 px-4">
							<div className="space-y-2">
								<Label htmlFor="user-code">Código</Label>
								<Input
									id="user-code"
									value={userFormCode}
									onChange={(e) => setUserFormCode(e.target.value)}
									disabled={!isCreatingUser}
									className={!isCreatingUser ? 'bg-muted' : ''}
									placeholder="Código del usuario"
									required={isCreatingUser}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="user-name">Nombre</Label>
								<Input
									id="user-name"
									value={userFormName}
									onChange={(e) => setUserFormName(e.target.value)}
									placeholder="Nombre del usuario"
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="user-email">Email</Label>
								<Input
									id="user-email"
									type="email"
									value={userFormEmail}
									onChange={(e) => setUserFormEmail(e.target.value)}
									placeholder="Email del usuario"
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="user-password">
									Contraseña
									{isCreatingUser && <span className="text-destructive">*</span>}
								</Label>
								<Input
									id="user-password"
									type="password"
									value={userFormPassword}
									onChange={(e) => setUserFormPassword(e.target.value)}
									placeholder={isCreatingUser ? 'Contraseña' : 'Dejar en blanco para no cambiar'}
									required={isCreatingUser}
								/>
								{!isCreatingUser && (
									<p className="text-xs text-muted-foreground">
										Dejar en blanco si no deseas cambiar la contraseña
									</p>
								)}
							</div>
							<div className="flex items-center justify-between p-3 rounded-lg border bg-card">
								<div className="space-y-0.5">
									<Label htmlFor="user-role" className="cursor-pointer">
										Tipo de usuario
									</Label>
									<p className="text-sm text-muted-foreground">
										{userFormIsManager ? 'Manager' : 'Empleado'}
									</p>
								</div>
								<Switch
									id="user-role"
									checked={userFormIsManager}
									onCheckedChange={setUserFormIsManager}
								/>
							</div>
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}
						</div>
						<SheetFooter className="pt-4">
							<Button
								onClick={handleSaveUser}
								disabled={
									userSaving ||
									!userFormName.trim() ||
									!userFormEmail.trim() ||
									(isCreatingUser && (!userFormCode.trim() || !userFormPassword.trim()))
								}
							>
								{userSaving ? 'Guardando...' : isCreatingUser ? 'Crear usuario' : 'Guardar cambios'}
							</Button>
						</SheetFooter>
					</SheetContent>
				</Sheet>

				{/* Sheet para crear/editar tipo de hora */}
				<Sheet
					open={isHourTypeSheetOpen}
					onOpenChange={(open) => {
						setIsHourTypeSheetOpen(open)
						if (!open) {
							setError(null)
							setIsCreatingHourType(false)
							setSelectedHourType(null)
						}
					}}
				>
					<SheetContent>
						<SheetHeader className="space-y-2 pb-4">
							<SheetTitle>{isCreatingHourType ? 'Crear tipo de hora' : 'Editar tipo de hora'}</SheetTitle>
							<SheetDescription>
								{isCreatingHourType
									? 'Completa los datos para crear un nuevo tipo de hora.'
									: 'Modifica los datos del tipo de hora. Los cambios se guardarán al hacer clic en Guardar.'}
							</SheetDescription>
						</SheetHeader>
						<div className="flex-1 py-4 space-y-4 px-4">
							<div className="space-y-2">
								<Label htmlFor="hour-type-code">Código</Label>
								<Input
									id="hour-type-code"
									value={hourTypeFormCode}
									onChange={(e) => setHourTypeFormCode(e.target.value)}
									placeholder="Código del tipo de hora"
									required={true}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="hour-type-name">Nombre</Label>
								<Input
									id="hour-type-name"
									value={hourTypeFormName}
									onChange={(e) => setHourTypeFormName(e.target.value)}
									placeholder="Nombre del tipo de hora"
									required
								/>
							</div>
							<div className="flex items-center justify-between p-3 rounded-lg border bg-card">
								<div className="space-y-0.5">
									<Label htmlFor="hour-type-computable" className="cursor-pointer">
										Computable
									</Label>
									<p className="text-sm text-muted-foreground">
										{hourTypeFormComputable
											? 'Este tipo de hora computa'
											: 'Este tipo de hora no computa'}
									</p>
								</div>
								<Switch
									id="hour-type-computable"
									checked={hourTypeFormComputable}
									onCheckedChange={setHourTypeFormComputable}
								/>
							</div>
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}
						</div>
						<SheetFooter className="pt-4">
							<Button
								onClick={handleSaveHourType}
								disabled={
									hourTypeSaving ||
									!hourTypeFormName.trim() ||
									(isCreatingHourType && !hourTypeFormCode.trim())
								}
							>
								{hourTypeSaving
									? 'Guardando...'
									: isCreatingHourType
										? 'Crear tipo de hora'
										: 'Guardar cambios'}
							</Button>
						</SheetFooter>
					</SheetContent>
				</Sheet>

				{/* Sheet para crear/editar proyecto */}
				<Sheet
					open={isProjectSheetOpen}
					onOpenChange={(open) => {
						setIsProjectSheetOpen(open)
						if (!open) {
							setError(null)
							setIsCreatingProject(false)
							setSelectedProject(null)
						}
					}}
				>
					<SheetContent>
						<SheetHeader className="space-y-2 pb-4">
							<SheetTitle>{isCreatingProject ? 'Crear proyecto' : 'Editar proyecto'}</SheetTitle>
							<SheetDescription>
								{isCreatingProject
									? 'Completa los datos para crear un nuevo proyecto.'
									: 'Modifica los datos del proyecto. Los cambios se guardarán al hacer clic en Guardar.'}
							</SheetDescription>
						</SheetHeader>
						<div className="flex-1 py-4 space-y-4 px-4">
							<div className="space-y-2">
								<Label htmlFor="project-code">Código</Label>
								<Input
									id="project-code"
									value={projectFormCode}
									onChange={(e) => setProjectFormCode(e.target.value)}
									placeholder="Código del proyecto"
									required={true}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="project-name">Nombre</Label>
								<Input
									id="project-name"
									value={projectFormName}
									onChange={(e) => setProjectFormName(e.target.value)}
									placeholder="Nombre del proyecto"
									required
								/>
							</div>
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}
						</div>
						<SheetFooter className="pt-4">
							<Button
								onClick={handleSaveProject}
								disabled={
									projectSaving ||
									!projectFormName.trim() ||
									(isCreatingProject && !projectFormCode.trim())
								}
							>
								{projectSaving
									? 'Guardando...'
									: isCreatingProject
										? 'Crear proyecto'
										: 'Guardar cambios'}
							</Button>
						</SheetFooter>
					</SheetContent>
				</Sheet>

				{/* Dialogs de confirmación */}
				<DeleteConfirmDialog
					open={deleteUserDialogOpen}
					onOpenChange={setDeleteUserDialogOpen}
					title="¿Estás seguro?"
					description="Esta acción no se puede deshacer. Se eliminará permanentemente este usuario."
					onConfirm={confirmDeleteUser}
				/>

				<DeleteConfirmDialog
					open={deleteHourTypeDialogOpen}
					onOpenChange={setDeleteHourTypeDialogOpen}
					title="¿Estás seguro?"
					description="Esta acción no se puede deshacer. Se eliminará permanentemente este tipo de hora."
					onConfirm={confirmDeleteHourType}
				/>

				<DeleteConfirmDialog
					open={deleteProjectDialogOpen}
					onOpenChange={setDeleteProjectDialogOpen}
					title="¿Estás seguro?"
					description="Esta acción no se puede deshacer. Se eliminará permanentemente este proyecto."
					onConfirm={confirmDeleteProject}
				/>

				{/* Sheet para opciones de usuario */}
				<UserOptionsSheet
					user={userOptionsUser}
					open={isUserOptionsSheetOpen}
					onOpenChange={(open) => {
						setIsUserOptionsSheetOpen(open)
						if (!open) {
							setUserOptionsUser(null)
						}
					}}
					projects={projects}
					hourTypes={hourTypes}
					companyID={company.id}
				/>

				{/* Dialog para mostrar detalles del registro horario */}
				<TimesheetDetailDialog
					timesheet={selectedTimesheet}
					open={isTimesheetDialogOpen}
					onOpenChange={setIsTimesheetDialogOpen}
					hourTypes={hourTypes}
					projects={projects}
					canManage={canManage}
					onUpdate={handleTimesheetUpdate}
					onDelete={() => {
						fetchTimesheets()
					}}
				/>
			</div>
		</div>
	)
}
